import { io, Socket } from "socket.io-client";
import { SnackbarSeverity } from "../components/snackbar";

// Define socket event types
export enum EventNames {
  // Challenge events
  CHALLENGE_CREATED = "challenge_created",
  CHALLENGE_UPDATED = "challenge_updated",
  CHALLENGE_VIEWED = "challenge_viewed",
  CHALLENGE_DONATED = "challenge_donated",
  CHALLENGE_BYPASS_REQUESTED = "challenge_bypass_requested",
  CHALLENGE_BYPASS_APPROVED = "challenge_bypass_approved",
  CHALLENGE_BYPASS_REJECTED = "challenge_bypass_rejected",

  // User events
  USER_LOGGED_IN = "user_logged_in",
  USER_LOGGED_OUT = "user_logged_out",
  USER_PROFILE_UPDATED = "user_profile_updated",
  USER_CREATED = "user_created",

  // Message events
  MESSAGE_RECEIVED = "message_received",
  MESSAGE_UPDATED = "message_updated",
  MESSAGE_READ = "message_read",
  MESSAGE_TAGGED = "message_tagged",
  SENT_MESSAGE_EXEMPT_FROM_RULE_CREATION = "sent_message_exempt_from_rule_creation",

  // Inbox events
  INBOX_CREATED = "inbox_created",
  INBOX_UPDATED = "inbox_updated",
  HEALTH_STATUS_UPDATED = "health_status_updated",

  // Onboarding events
  ONBOARDING_MILESTONE_STARTED = "onboarding_milestone_started",
  ONBOARDING_MILESTONE_COMPLETED = "onboarding_milestone_completed",
  ONBOARDING_MILESTONE_FAILED = "onboarding_milestone_failed",
  ONBOARDING_MILESTONE_PROGRESS = "onboarding_milestone_progress",
  ONBOARDING_SETUP_COMPLETE = "onboarding_setup_complete",
}

// Initialize socket connection
const socket: Socket = io(process.env.REACT_APP_COBUTLER_API_URL, {
  withCredentials: true,
  autoConnect: false,
  path: "/sockets",
});

socket.onAny((event, data) => {
  console.log("Socket event:", event, data);
});

// Add authentication token to socket connection
socket.on("connect", () => {
  const authToken = localStorage.getItem("cobutlerToken");
  if (authToken) {
    socket.emit("authenticate", { token: authToken });
  }
});

// Handle authentication errors
socket.on("auth_error", (error) => {
  console.error("Socket authentication error:", error);
  if (notificationCallback) {
    notificationCallback("Socket authentication failed", "error");
  }
});

// Handle connection errors
socket.on("connect_error", (error) => {
  console.error("Socket connection error:", error);
  if (notificationCallback) {
    notificationCallback("Socket connection failed", "error");
  }
});

// Handle disconnection
socket.on("disconnect", (reason) => {
  console.log("Socket disconnected:", reason);
  if (notificationCallback) {
    notificationCallback("Disconnected from server", "error");
  }
});

// Handle reconnection
socket.on("reconnect", (attemptNumber) => {
  console.log("Socket reconnected after", attemptNumber, "attempts");
  if (notificationCallback) {
    notificationCallback("Reconnected to server", "success");
  }
});

// Callback for notifications
let notificationCallback:
  | ((message: string, severity: SnackbarSeverity) => void)
  | null = null;

export const setNotificationCallback = (
  callback: (message: string, severity: SnackbarSeverity) => void,
) => {
  notificationCallback = callback;
};

export const connectSocket = () => {
  if (!socket.connected) {
    socket.connect();
  }
};

export const disconnectSocket = () => {
  socket.disconnect();
};

export const subscribeToEvent = (
  event: EventNames,
  callback: (data: any) => void,
) => {
  socket.on(event, callback);
};

export const unsubscribeFromEvent = (
  event: EventNames,
  callback: (data: any) => void,
) => {
  socket.off(event, callback);
};

// Helper function to show snackbar for socket events
export const showSocketEventSnackbar = (event: EventNames, data: any) => {
  if (!notificationCallback) return;

  switch (event) {
    case EventNames.MESSAGE_RECEIVED:
      notificationCallback("New message received", "info");
      break;
    case EventNames.MESSAGE_UPDATED:
      notificationCallback("Message updated", "info");
      break;
    case EventNames.INBOX_UPDATED:
      notificationCallback("Inbox updated", "info");
      break;
    case EventNames.HEALTH_STATUS_UPDATED:
      const status = data.status;
      if (status === "healthy") {
        notificationCallback("Inbox is healthy", "success");
      } else if (status === "unhealthy") {
        notificationCallback("Inbox is unhealthy", "error");
      } else if (status === "atRisk") {
        notificationCallback("Inbox is at risk", "warning");
      }
      break;
    case EventNames.CHALLENGE_CREATED:
      notificationCallback("Challenge created", "success");
      break;
    case EventNames.CHALLENGE_UPDATED:
      notificationCallback("Challenge updated", "info");
      break;
    case EventNames.ONBOARDING_MILESTONE_PROGRESS:
      const progressInfo =
        data.messagesSynced !== undefined && data.totalToSync !== undefined
          ? `${data.progressPercent}% (${data.messagesSynced}/${data.totalToSync})`
          : `${data.progressPercent}%`;
      notificationCallback(`Sync progress: ${progressInfo}`, "info");
      break;
  }
};

export default socket;
